import axios, { AxiosRequestConfig } from 'axios';
import Token from 'types/models/Token';
import BaseApiService from './BaseApiService';
import InMemoryAuthApiService from './InMemory/InMemoryAuthApiService';
import getCompanyByHeader from 'helpers/getCompanyByHeader';
import { getSubdomain } from 'helpers/utils';
import { secureTokenStorage } from 'utils/secureTokenStorage';

export interface IAuthApiService {
  getAuthAuthorization(): Promise<string>;
  getAuthAuthorizationAfterLogout(): Promise<string>;
  getAuthToken(tokenSource: string | Token): Promise<Token>;
  getTokenUri(code: string): Promise<{ tokenUri: string | Token }>;
  getAuthCode(authUri: string): Promise<{ tokenUri: string }>;
  getRefreshTokenUri(refresh_token: string): Promise<{
    tokenUri?: string | Token;
    error?: string;
  }>;
  logout(): Promise<string>;
}
class AuthApiService extends BaseApiService {
  private baseUrl: string;

  public companyName: string | null = null;

  private redirectUri: string | null = null;

  private axiosAuthConfig: AxiosRequestConfig;

  constructor() {
    super();
    const currentHostname = window.location.hostname;
    const currentSub = getSubdomain(currentHostname);
    const company = getCompanyByHeader(currentSub);
    this.baseUrl = `${process.env.REACT_APP_SERVER_URL}/auth`;
    const { domainPrefix, redirectUri } = company.cognito;
    this.redirectUri = redirectUri;
    this.companyName = domainPrefix;
    this.axiosAuthConfig = {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Access-Control-Allow-Origin': '*',
        'X-Sub-Domain': domainPrefix,
      },
    };
  }

  async getAuthAuthorization(): Promise<string> {
    const res = await axios.get(`${this.baseUrl}/authorize?redirect_uri=${this.redirectUri}`, {
      ...this.axiosAuthConfig,
      headers: {
        'X-Sub-Domain': this.companyName,
      },
    });
    return res.data.authUri;
  }

  async getAuthAuthorizationAfterLogout(): Promise<string> {
    const authUri = await this.getAuthAuthorization();
    return AuthApiService.getLogoutUriFromAuthUri(authUri);
  }

  async getAuthAuthorizationLogout(): Promise<string> {
    const tokenObj = secureTokenStorage.get();
    let authHeader = {};
    if (tokenObj?.id_token) {
      authHeader = {
        Authorization: `Bearer ${tokenObj.id_token}`,
      };
    }
    // const res = await axios.get(`${this.baseUrl}/authorize?redirect_uri=${this.redirectUri}`, this.axiosAuthConfig);
    const res = await axios.get(`${this.baseUrl}/authorize?redirect_uri=${this.redirectUri}`, {
      ...this.axiosAuthConfig,
      headers: {
        ...this.axiosAuthConfig.headers,
        ...authHeader,
      },
    });
    return AuthApiService.getLogoutUriFromAuthUri(res.data.authUri);
  }

  async getAuthToken(tokenSource: string | Token) {
    try {
      // New secure behaviour: when backend already exchanged the code/refresh token,
      // we receive the token payload directly and just persist it on the client.
      if (typeof tokenSource !== 'string') {
        this.setToken(tokenSource);
        return tokenSource;
      }

      // Legacy fallback (should no longer be used once backend is updated):
      // avoid sending any client_secret from the browser.
      const res = await axios.post(tokenSource, {}, this.axiosAuthConfig);
      this.setToken(res.data);
      return res.data;
    } catch (error: any) {
      if (error.response) {
        return error.response.data;
      }
      return error;
    }
  }

  async getTokenUri(code: string): Promise<{ tokenUri: string | Token }> {
    const res = await axios.post(
      `${this.baseUrl}/token?code=${code}&redirect_uri=${this.redirectUri}`,
      this.axiosAuthConfig,
      {
        headers: {
          'X-Sub-Domain': this.companyName,
        },
      }
    );
    return res.data;
  }

  async getAuthCode(authUri: string): Promise<{ tokenUri: string }> {
    return axios.get(authUri, this.axiosAuthConfig);
  }

  async getRefreshTokenUri(refresh_token: string): Promise<{
    tokenUri?: string | Token;
    error?: string;
  }> {
    try {
      const res = await axios.post(
        `${this.baseUrl}/refresh`,
        { refresh_token },
        {
          headers: { 'X-Sub-Domain': this.companyName },
          timeout: 10000,
        }
      );
      if (res.data?.tokenUri) return { tokenUri: res.data.tokenUri };
      return { error: 'no_response' };
    } catch (error: any) {
      if (error.response) {
        return { error: error.response?.data?.error || 'no_response' };
      }
      if (error.request) return { error: 'no_response' };
      return { error: error.message ?? 'no_response' };
    }
  }

  // eslint-disable-next-line class-methods-use-this
  public setToken(data: Token): void {
    const existing = secureTokenStorage.get();
    let dataToStock = data;
    if (existing) {
      dataToStock = { ...existing, ...data };
    }
    // eslint-disable-next-line no-param-reassign
    data.expires_at = (Date.now() + (data.expires_in as number)) as number;
    secureTokenStorage.set(dataToStock);
  }

  /**
   * Logout user - revokes token on backend and returns Cognito logout URL
   * @returns Promise<string> - Cognito logout URL to redirect to
   */
  public async logout(): Promise<string> {
    try {
      // Clear local data even if backend call fails
      this.clearAllAuthData();

      const idToken = this.getIdToken();
      if (idToken) {
        // Call backend to revoke token
        console.log('Calling backend to revoke token');
        const res = await axios.post(
          `${this.baseUrl}/logout`,
          {},
          {
            headers: {
              Authorization: `Bearer ${idToken}`,
              'X-Sub-Domain': this.companyName,
            },
          }
        );

        console.log('Token revoked on backend:', res.data.message);
        console.log('Logout URI from backend:', res.data.logoutUri);

        // Return Cognito logout URI from backend
        return this.getAuthAuthorization();
      }
      console.warn('No token found for revocation');
      // Redirect to login/authorize page instead of logout
      return this.getAuthAuthorization();
    } catch (error: any) {
      console.error('Logout error:', error);
      console.error('Error details:', error.response?.data);

      // Try to get logout URL from error response
      if (error.response?.data?.logoutUri) {
        console.log('Using logoutUri from error response');
        return error.response.data.logoutUri;
      }

      // Fallback to authorization URL (login page)
      console.log('Falling back to authorization URL');
      return this.getAuthAuthorization();
    }
  }

  /**
   * Clear all authentication-related data from storage
   */
  // eslint-disable-next-line class-methods-use-this
  private clearAllAuthData(): void {
    // Clear in-memory token
    secureTokenStorage.clear();
    localStorage.removeItem('active_customer');
    localStorage.removeItem('navigation_screen');

    // Clear sessionStorage
    sessionStorage.removeItem('threadIdAI');
    sessionStorage.clear();

    console.log('All authentication data cleared from storage');
  }

  // eslint-disable-next-line class-methods-use-this
  private getIdToken(): string | null {
    return secureTokenStorage.get()?.id_token ?? null;
  }

  private static getLogoutUriFromAuthUri(authUri: string): string {
    try {
      const authUrl = new URL(authUri);
      const logoutUrl = new URL('/logout', authUrl.origin);

      // Cognito /logout only accepts client_id and logout_uri.
      // Copying all authorize params (response_type, redirect_uri, state, scope)
      // causes "Required parameters missing" on the Cognito hosted UI.
      const clientId = authUrl.searchParams.get('client_id');
      const redirectUri = authUrl.searchParams.get('redirect_uri');

      if (clientId) logoutUrl.searchParams.set('client_id', clientId);
      if (redirectUri) logoutUrl.searchParams.set('logout_uri', redirectUri);

      return logoutUrl.toString();
    } catch (error) {
      console.error('Failed to create Cognito logout URL:', error);
      return authUri;
    }
  }
}
const isTestMode = process.env.REACT_APP_NODE_ENV === 'test';
const authApiService = isTestMode ? new InMemoryAuthApiService() : new AuthApiService();

export default authApiService;
